import React, { useEffect, useMemo, useState } from "react";

import {
  ArrowRight,
  Code2,
  Download,
  Loader2,
  Plus,
  Search,
  X,
} from "lucide-react";

import {
  classifierChanges,
  type DraftRule,
  draftRules,
  loopProblems,
  nextRuleId,
  pickLead,
  ruleProblems,
  signalTag,
  STATUS_CLASS,
  stageRule,
  TIER_DOT,
  toRecord,
  TYPE_CLASS,
  TYPE_HINT,
  TYPE_ORDER,
  TYPE_SECTION,
} from "./classifier";
import Drawer from "./Drawer";
import { errorText } from "./format";
import OrgSelect from "./OrgSelect";
import {
  type ClassifierImpact,
  type ClassifierRuleRecord,
  type ClassifierRuleStatus,
  type ClassifierRuleType,
  type ClassifierTry,
  type SignalAdminConfig,
  type SignalAdminDraft,
  type SignalAdminOrganization,
  type SignalAdminRegistry,
  type SignalDefinition,
  draftTier,
  TIER_META,
} from "./types";
import { useAppDispatch } from "../../Store/Hooks";
import {
  fetchClassifierImpact,
  tryClassifierOnAccount,
} from "../../Store/Reducers/SignalAdminSlice";

interface RuleClassifierPanelProps {
  registry: SignalAdminRegistry;
  /** Applied config, to tell staged changes from applied ones. */
  config: SignalAdminConfig;
  draft: SignalAdminDraft;
  onDraftChange: (next: SignalAdminDraft) => void;
  /** Impact and "try on an account" are read per organization. */
  organizations: SignalAdminOrganization[];
  orgsLoading: boolean;
  orgId: string;
  onOrgChange: (orgId: string) => void;
  /** Opens a signal's Registry page (Signal settings / Card / Action plan)
   * in the right-hand pane, without leaving the Rule Classifier. */
  onOpenSignal?: (id: string) => void;
}

type Tab = "decide" | "force" | "signal";

const BTN =
  "inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-[12.5px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40";
const PRIMARY = `${BTN} bg-brand text-white hover:bg-brand-dark`;
const SECONDARY = `${BTN} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50`;
const DANGER = `${BTN} border border-red-200 bg-white text-red-700 hover:bg-red-50`;
const CHIP = "rounded-full px-2 py-0.5 text-[11px] font-semibold";
const TIER_RANK = ["critical", "warning", "opp", "maint"];

const SOURCE_TEXT: Record<DraftRule["source"], string> = {
  code: "Code default",
  changed: "Changed here · not in code",
  added: "Added here · not in code",
};

const EmptyState: React.FC<{ title: string; body: string }> = ({
  title,
  body,
}) => (
  <div className="rounded-xl border border-dashed border-gray-300 px-6 py-10 text-center">
    <div className="text-[13.5px] font-semibold text-gray-800">{title}</div>
    <p className="mx-auto mt-1 max-w-md text-[12.5px] text-gray-500">{body}</p>
  </div>
);

const Toggle: React.FC<{
  on: boolean;
  label: string;
  disabled?: boolean;
  onChange: (on: boolean) => void;
}> = ({ on, label, disabled, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!on)}
    className="flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-40"
  >
    <span
      className={`relative h-5 w-9 rounded-full transition-colors ${
        on ? "bg-emerald-600" : "bg-gray-300"
      }`}
    >
      <span
        className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
          on ? "left-[18px]" : "left-0.5"
        }`}
      />
    </span>
    <span className="w-6 text-left text-[12px] font-semibold text-gray-700">
      {on ? "On" : "Off"}
    </span>
  </button>
);

const SignalChip: React.FC<{
  id: string;
  byId: Record<string, SignalDefinition>;
  draft: SignalAdminDraft;
  muted?: boolean;
  /** When set the chip is a button that opens the signal's Registry page. */
  onOpen?: (id: string) => void;
}> = ({ id, byId, draft, muted, onOpen }) => {
  const d = byId[id];
  if (!d)
    return (
      <span className="rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-[12px] text-red-700">
        {id} (removed)
      </span>
    );
  const tier = draftTier(d, draft);
  const className = `inline-flex max-w-[340px] items-center gap-1.5 rounded-lg border px-2 py-1 text-[12px] ${
    muted
      ? "border-gray-200 bg-gray-50 text-gray-500"
      : "border-gray-300 bg-white text-gray-800"
  }`;
  const body = (
    <>
      <span
        className={`h-2 w-2 shrink-0 rounded-full ${TIER_DOT[tier]}`}
        aria-hidden
      />
      <span className="font-bold">{signalTag(d)}</span>
      <span className="truncate">{d.title}</span>
    </>
  );
  if (onOpen)
    return (
      <button
        type="button"
        onClick={() => onOpen(id)}
        title={`Open ${signalTag(d)} ${d.title} in Registry`}
        aria-label={`Open ${signalTag(d)} ${d.title} in Registry`}
        className={`${className} hover:border-brand hover:text-brand focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/30`}
      >
        {body}
      </button>
    );
  return (
    <span
      title={`${signalTag(d)} ${d.title} · ${TIER_META[tier].label}`}
      className={className}
    >
      {body}
    </span>
  );
};

const RuleFlow: React.FC<{
  rule: ClassifierRuleRecord;
  byId: Record<string, SignalDefinition>;
  draft: SignalAdminDraft;
  onOpenSignal?: (id: string) => void;
}> = ({ rule, byId, draft, onOpenSignal }) => {
  const others = rule.members.filter(m => m !== rule.lead);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <SignalChip
        id={rule.lead}
        byId={byId}
        draft={draft}
        onOpen={onOpenSignal}
      />
      <span className="flex items-center gap-1 text-[12px] font-semibold text-gray-500">
        {rule.type === "duplicate" ? "replaces" : "covers"}
        <ArrowRight className="h-4 w-4" aria-hidden />
      </span>
      {others.map(m => (
        <SignalChip
          key={m}
          id={m}
          byId={byId}
          draft={draft}
          muted
          onOpen={onOpenSignal}
        />
      ))}
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section className="flex flex-col gap-2">
    <h5 className="text-[10.5px] font-bold uppercase tracking-wide text-gray-500">
      {title}
    </h5>
    {children}
  </section>
);

const BySignal: React.FC<{
  rules: DraftRule[];
  byId: Record<string, SignalDefinition>;
  draft: SignalAdminDraft;
  onOpen: (id: string) => void;
  onOpenSignal?: (id: string) => void;
}> = ({ rules, byId, draft, onOpen, onOpenSignal }) => {
  const rows = new Map<string, { hides: DraftRule[]; hiddenBy: DraftRule[] }>();
  const row = (id: string) => {
    if (!rows.has(id)) rows.set(id, { hides: [], hiddenBy: [] });
    return rows.get(id) as {
      hides: DraftRule[];
      hiddenBy: DraftRule[];
    };
  };
  rules
    .filter(r => r.status !== "off")
    .forEach(r => {
      row(r.lead).hides.push(r);
      r.members.filter(m => m !== r.lead).forEach(m => row(m).hiddenBy.push(r));
    });
  const tag = (id: string) => (byId[id] ? signalTag(byId[id]) : id);
  const ordered = Array.from(rows.keys()).sort((a, b) =>
    (byId[a]?.order ?? a).localeCompare(byId[b]?.order ?? b, undefined, {
      numeric: true,
    })
  );
  const ruleChip = (r: DraftRule, label: string) => (
    <button
      key={r.id}
      type="button"
      onClick={() => onOpen(r.id)}
      className={`rounded-md border px-1.5 py-0.5 text-[11.5px] hover:bg-gray-50 ${
        r.status === "proposed"
          ? "border-dashed border-sky-300 text-sky-800"
          : "border-gray-300 text-gray-800"
      }`}
      title={r.status === "proposed" ? "Proposed — not in force" : "In force"}
    >
      <span className="font-mono font-bold">{r.id}</span> {label}
    </button>
  );
  const none = <span className="text-gray-300">—</span>;
  if (ordered.length === 0)
    return (
      <EmptyState
        title="No signals match"
        body="Clear the search to see every signal a rule touches."
      />
    );
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
      <table className="w-full min-w-[560px] text-left text-[12.5px]">
        <thead className="border-b border-gray-200 bg-gray-50 text-[10.5px] font-bold uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-3 py-2">Signal</th>
            <th className="px-3 py-2">Hides</th>
            <th className="px-3 py-2">Hidden by</th>
          </tr>
        </thead>
        <tbody>
          {ordered.map(id => {
            const r = row(id);
            return (
              <tr
                key={id}
                className="border-b border-gray-100 align-top last:border-0"
              >
                <td className="px-3 py-2">
                  <SignalChip
                    id={id}
                    byId={byId}
                    draft={draft}
                    onOpen={onOpenSignal}
                  />
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {r.hides.length
                      ? r.hides.map(x =>
                          ruleChip(
                            x,
                            x.members
                              .filter(m => m !== x.lead)
                              .map(tag)
                              .join(", ")
                          )
                        )
                      : none}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {r.hiddenBy.length
                      ? r.hiddenBy.map(x => ruleChip(x, tag(x.lead)))
                      : none}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="border-t border-gray-100 px-3 py-2 text-[11.5px] text-gray-500">
        Dashed = proposed, not in force yet. Switched-off rules are left out.
      </p>
    </div>
  );
};

const OUTCOME: Record<string, { label: string; className: string }> = {
  shown: { label: "Shown", className: "bg-emerald-50 text-emerald-700" },
  covered: { label: "Hidden", className: "bg-gray-100 text-gray-600" },
};

const RuleDrawer: React.FC<{
  rule: DraftRule;
  registry: SignalAdminRegistry;
  byId: Record<string, SignalDefinition>;
  draft: SignalAdminDraft;
  impact: ClassifierImpact | null;
  orgId: string;
  changes: Record<string, ClassifierRuleRecord | null>;
  onClose: () => void;
  onStatus: (next: ClassifierRuleStatus) => void;
  onReset: () => void;
  onEdit: () => void;
  onOpenSignal?: (id: string) => void;
}> = ({
  rule,
  registry,
  byId,
  draft,
  impact,
  orgId,
  changes,
  onClose,
  onStatus,
  onReset,
  onEdit,
  onOpenSignal,
}) => {
  const dispatch = useAppDispatch();
  const problems = rule.status === "active" ? [] : ruleProblems(rule);
  const v = impact?.rules[rule.id];
  const firstExample = v?.examples[0]?.id ?? "";
  const [accountId, setAccountId] = useState("");
  const [tried, setTried] = useState<ClassifierTry | null>(null);
  const [trying, setTrying] = useState(false);
  const [tryError, setTryError] = useState("");
  const asOnKey = JSON.stringify({
    ...changes,
    [rule.id]: { ...toRecord(rule), status: "active" },
  });

  // Start on an account this rule actually changes.
  useEffect(() => {
    setAccountId(firstExample);
  }, [rule.id, firstExample]);

  // The rule is judged as switched on, whatever its status, so a proposal
  // shows what accepting it would do.
  useEffect(() => {
    if (!orgId || !accountId) {
      setTried(null);
      return undefined;
    }
    let cancelled = false;
    setTrying(true);
    setTryError("");
    dispatch(
      tryClassifierOnAccount({
        orgId,
        accountId,
        classifier: JSON.parse(asOnKey),
      })
    )
      .unwrap()
      .then(res => {
        if (!cancelled) setTried(res as ClassifierTry);
      })
      .catch(e => {
        if (!cancelled)
          setTryError(errorText(e, "Couldn't preview this account."));
      })
      .finally(() => {
        if (!cancelled) setTrying(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, orgId, accountId, asOnKey]);

  const title =
    rule.type === "duplicate"
      ? "Show one of these signals"
      : "Hide signals while a stronger one fires";
  const shownAfter = tried?.after.filter(c => c.outcome === "shown").length;
  return (
    <Drawer
      open
      onClose={onClose}
      eyebrow={
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="rounded-md bg-gray-100 px-1.5 py-0.5 font-mono text-[11.5px] font-bold text-gray-700">
            {rule.id}
          </span>
          <span className={`${CHIP} ${TYPE_CLASS[rule.type]}`}>
            {registry.classifier.types[rule.type]}
          </span>
          <span className={`${CHIP} ${STATUS_CLASS[rule.status]}`}>
            {registry.classifier.statuses[rule.status]}
          </span>
        </span>
      }
      title={title}
      subtitle={SOURCE_TEXT[rule.source]}
    >
      <div className="flex flex-col gap-5">
        <Section title="How it works">
          <RuleFlow
            rule={rule}
            byId={byId}
            draft={draft}
            onOpenSignal={
              onOpenSignal
                ? id => {
                    onClose();
                    onOpenSignal(id);
                  }
                : undefined
            }
          />
          <p className="text-[12.5px] text-gray-600">
            {TYPE_HINT[rule.type]} A rule never hides a signal firing at a
            higher tier than its lead.
          </p>
        </Section>

        {problems.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
            <div className="font-semibold">Can&rsquo;t be switched on yet</div>
            {problems.map(p => (
              <div key={p} className="mt-0.5">
                {p}
              </div>
            ))}
          </div>
        )}

        <Section title="Impact today">
          {!orgId ? (
            <p className="text-[12.5px] text-gray-500">
              Pick an organization above to see impact.
            </p>
          ) : !v ? (
            <p className="text-[12.5px] text-gray-500">Working out impact…</p>
          ) : (
            <p className="text-[12.5px] text-gray-800">
              {v.cards === 0
                ? "Changes nothing on today’s cards in this organization."
                : `Hides ${v.cards} card${
                    v.cards === 1 ? "" : "s"
                  } on ${v.accounts} of ${
                    impact?.accounts_with_signals ?? 0
                  } accounts with signals${
                    rule.status === "active" ? "" : " if switched on"
                  }.`}
            </p>
          )}
        </Section>

        {orgId && (
          <Section title="Try on an account">
            <select
              aria-label="Account to try"
              value={accountId}
              onChange={e => setAccountId(e.target.value)}
              className="h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-[12.5px]"
            >
              <option value="">Pick an account…</option>
              {v && v.examples.length > 0 && (
                <optgroup label="Changed by this rule">
                  {v.examples.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label="All accounts with signals">
                {(impact?.accounts ?? []).map(a => (
                  <option key={a.id} value={a.id}>
                    {a.name} · {a.open} open
                  </option>
                ))}
              </optgroup>
            </select>
            {trying && (
              <span className="text-[12px] text-gray-500">Loading…</span>
            )}
            {tryError && (
              <span className="text-[12px] text-red-700">{tryError}</span>
            )}
            {tried && !trying && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <div className="mb-1 text-[11.5px] font-semibold text-gray-600">
                    Now · {tried.before.length} card
                    {tried.before.length === 1 ? "" : "s"}
                  </div>
                  <ul className="flex flex-col gap-1">
                    {tried.before.map(c => (
                      <li
                        key={c.definition_id}
                        className="rounded-md border border-gray-200 px-2 py-1.5 text-[12px]"
                      >
                        <span
                          className={`mr-1.5 inline-block h-2 w-2 rounded-full ${TIER_DOT[c.tier]}`}
                          aria-hidden
                        />
                        {c.label}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="mb-1 text-[11.5px] font-semibold text-gray-600">
                    With this rule on · {shownAfter} card
                    {shownAfter === 1 ? "" : "s"}
                  </div>
                  <ul className="flex flex-col gap-1">
                    {tried.after.map(c => (
                      <li
                        key={c.definition_id}
                        className={`rounded-md border px-2 py-1.5 text-[12px] ${
                          c.outcome === "shown"
                            ? "border-gray-200"
                            : "border-dashed border-gray-200 text-gray-500"
                        }`}
                      >
                        <span
                          className={`mr-1.5 ${CHIP} ${OUTCOME[c.outcome].className}`}
                        >
                          {OUTCOME[c.outcome].label}
                        </span>
                        {c.label}
                        {c.by && (
                          <span className="block pl-1 text-[11px] text-gray-500">
                            by {c.by} ({c.rule})
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </Section>
        )}

        {rule.note && (
          <Section title="Why">
            <p className="text-[12.5px] leading-relaxed text-gray-700">
              {rule.note}
            </p>
          </Section>
        )}

        <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-4">
          {rule.status === "proposed" && (
            <>
              <button
                type="button"
                className={PRIMARY}
                disabled={problems.length > 0}
                onClick={() => onStatus("active")}
              >
                Accept
              </button>
              <button
                type="button"
                className={SECONDARY}
                onClick={() => onStatus("off")}
              >
                Reject
              </button>
            </>
          )}
          {rule.status === "active" && (
            <button
              type="button"
              className={SECONDARY}
              onClick={() => onStatus("off")}
            >
              Switch off
            </button>
          )}
          {rule.status === "off" && (
            <button
              type="button"
              className={SECONDARY}
              disabled={problems.length > 0}
              onClick={() => onStatus("active")}
            >
              Switch on
            </button>
          )}
          <button type="button" className={SECONDARY} onClick={onEdit}>
            Edit
          </button>
          {rule.source === "changed" && (
            <button type="button" className={SECONDARY} onClick={onReset}>
              Reset to code default
            </button>
          )}
          {rule.source === "added" && (
            <button type="button" className={DANGER} onClick={onReset}>
              Remove rule
            </button>
          )}
        </div>
        <p className="text-[11.5px] text-gray-500">
          Changes are staged. They reach users after Review and apply, and are
          recorded in Change Audit.
        </p>
      </div>
    </Drawer>
  );
};

const TYPES: ClassifierRuleType[] = ["supersede", "duplicate"];

const RuleEditor: React.FC<{
  ruleId: string;
  isNew: boolean;
  initial: ClassifierRuleRecord;
  registry: SignalAdminRegistry;
  draft: SignalAdminDraft;
  otherRules: ClassifierRuleRecord[];
  onCancel: () => void;
  onSave: (record: ClassifierRuleRecord) => void;
}> = ({
  ruleId,
  isNew,
  initial,
  registry,
  draft,
  otherRules,
  onCancel,
  onSave,
}) => {
  const byId = useMemo(
    () => Object.fromEntries(registry.definitions.map(d => [d.id, d])),
    [registry.definitions]
  );
  const [type, setType] = useState<ClassifierRuleType>(initial.type);
  const [members, setMembers] = useState<string[]>(initial.members);
  const [lead, setLead] = useState<string>(initial.lead);
  const [note, setNote] = useState(initial.note);
  const [adding, setAdding] = useState("");

  const recommended = pickLead(members, byId);
  const effectiveLead = members.includes(lead) ? lead : recommended;
  const record: ClassifierRuleRecord = {
    type,
    lead: effectiveLead,
    members,
    status: initial.status,
    note: note.trim(),
  };
  const problems = [
    ...ruleProblems(record),
    ...(record.status === "active"
      ? loopProblems([...otherRules, record], registry)
      : []),
  ];
  const leadTier = byId[effectiveLead]
    ? draftTier(byId[effectiveLead], draft)
    : null;
  const higher = members.filter(m => {
    const d = byId[m];
    if (!d || m === effectiveLead || !leadTier) return false;
    return TIER_RANK.indexOf(draftTier(d, draft)) < TIER_RANK.indexOf(leadTier);
  });
  // A proposed or switched-off rule only needs a valid shape; the loop
  // check applies when it's switched on.
  const blocking = record.status === "active" ? problems : ruleProblems(record);
  const sorted = [...registry.definitions].sort((a, b) =>
    a.order.localeCompare(b.order, undefined, { numeric: true })
  );
  const tag = (m: string) => (byId[m] ? signalTag(byId[m]) : m);

  return (
    <Drawer
      open
      onClose={onCancel}
      eyebrow={
        <span className="text-[11px] text-gray-500">
          Rule Classifier · {isNew ? "new rule" : "edit rule"}
        </span>
      }
      title={isNew ? `New rule ${ruleId}` : `Edit ${ruleId}`}
    >
      <div className="flex flex-col gap-5">
        <Section title="What should happen">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {TYPES.map(t => (
              <button
                key={t}
                type="button"
                aria-pressed={type === t}
                onClick={() => setType(t)}
                className={`flex flex-col gap-1 rounded-lg border px-3 py-2 text-left ${
                  type === t
                    ? "border-brand bg-brand-tint"
                    : "border-gray-300 bg-white hover:bg-gray-50"
                }`}
              >
                <span className="text-[13px] font-semibold text-gray-900">
                  {registry.classifier.types[t]}
                </span>
                <span className="text-[11.5px] leading-snug text-gray-600">
                  {TYPE_HINT[t]}
                </span>
              </button>
            ))}
          </div>
        </Section>

        <Section title="Signals">
          <div className="flex flex-col gap-1.5">
            {members.map(m => (
              <div
                key={m}
                className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-2.5 py-1.5"
              >
                <label className="flex min-w-0 items-center gap-2 text-[12.5px]">
                  <input
                    type="radio"
                    name="lead"
                    checked={effectiveLead === m}
                    onChange={() => setLead(m)}
                    aria-label={`Make ${tag(m)} the lead`}
                  />
                  <SignalChip id={m} byId={byId} draft={draft} />
                  {m === recommended && (
                    <span className="text-[11px] text-gray-500">
                      recommended lead
                    </span>
                  )}
                </label>
                <button
                  type="button"
                  aria-label={`Remove ${tag(m)}`}
                  onClick={() => setMembers(members.filter(x => x !== m))}
                  className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
            ))}
            {members.length === 0 && (
              <p className="text-[12px] text-gray-500">
                Add at least two signals.
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <select
              aria-label="Add a signal"
              value={adding}
              onChange={e => setAdding(e.target.value)}
              className="h-9 min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-2.5 text-[12.5px]"
            >
              <option value="">Add a signal…</option>
              {sorted
                .filter(d => !members.includes(d.id))
                .map(d => (
                  <option key={d.id} value={d.id}>
                    {signalTag(d)} {d.title} ·{" "}
                    {TIER_META[draftTier(d, draft)].label}
                  </option>
                ))}
            </select>
            <button
              type="button"
              className={SECONDARY}
              disabled={!adding}
              onClick={() => {
                setMembers([...members, adding]);
                setAdding("");
              }}
            >
              Add
            </button>
          </div>
          <p className="text-[11.5px] text-gray-500">
            The lead (radio) is the signal users keep seeing.
          </p>
          {higher.length > 0 && (
            <p className="text-[11.5px] text-amber-700">
              {higher.map(tag).join(", ")} fire
              {higher.length === 1 ? "s" : ""} at a higher tier than the lead,
              so this rule will never hide {higher.length === 1 ? "it" : "them"}
              .
            </p>
          )}
        </Section>

        <label className="flex flex-col gap-1">
          <span className="text-[10.5px] font-bold uppercase tracking-wide text-gray-500">
            Why this rule
          </span>
          <textarea
            rows={2}
            maxLength={400}
            value={note}
            onChange={e => setNote(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-[12.5px]"
          />
        </label>

        {members.length >= 2 && (
          <Section title="Preview">
            <RuleFlow rule={record} byId={byId} draft={draft} />
          </Section>
        )}

        <Section title="Checks">
          {problems.length === 0 ? (
            <span className="text-[12.5px] text-emerald-700">
              Ready to switch on.
            </span>
          ) : (
            problems.map(p => (
              <span key={p} className="text-[12.5px] text-rose-700">
                {p}
              </span>
            ))
          )}
          {record.status !== "active" && problems.length > 0 && (
            <span className="text-[11.5px] text-gray-500">
              It can be saved as {record.status}; fix these before switching it
              on.
            </span>
          )}
        </Section>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
          <button type="button" className={SECONDARY} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={PRIMARY}
            disabled={blocking.length > 0}
            onClick={() => onSave(record)}
          >
            {isNew ? "Add to draft" : "Save to draft"}
          </button>
        </div>
      </div>
    </Drawer>
  );
};

/** The applied rules as backend/utils/signal_rules.json, for a developer
 * to commit. Once that ships, the matching admin changes fold away. */
const ExportDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
  registry: SignalAdminRegistry;
  name: (id: string) => string;
}> = ({ open, onClose, registry, name }) => {
  const text = `${JSON.stringify(registry.classifier.export, null, 1)}\n`;
  const changed = registry.classifier.rules.filter(r => r.source !== "code");
  const download = () => {
    const url = URL.createObjectURL(
      new Blob([text], { type: "application/json" })
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = "signal_rules.json";
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Export to code"
      subtitle="backend/utils/signal_rules.json"
    >
      <div className="flex flex-col gap-4 text-[12.5px] text-gray-700">
        <p>
          {changed.length === 0
            ? "The applied rules match the code. Nothing to export."
            : `${changed.length} applied change${
                changed.length === 1 ? " isn’t" : "s aren’t"
              } in code yet. Download the rule file and hand it to a developer to commit in a normal PR. Nothing here changes the code by itself.`}
        </p>
        {changed.length > 0 && (
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            {changed.map(r => (
              <li
                key={r.id}
                className="flex flex-wrap gap-x-3 gap-y-0.5 px-3 py-2"
              >
                <span className="font-mono font-bold text-gray-900">
                  {r.id}
                </span>
                <span className="min-w-0 flex-1">
                  {r.members.map(name).join(" + ")}
                </span>
                <span className="text-gray-500">
                  {r.source === "added"
                    ? `Added · ${registry.classifier.statuses[r.status]}`
                    : `${registry.classifier.statuses[r.code?.status ?? r.status]} → ${
                        registry.classifier.statuses[r.status]
                      }`}
                </span>
              </li>
            ))}
          </ul>
        )}
        <pre className="max-h-[320px] overflow-auto rounded-lg bg-gray-900 p-3 font-mono text-[11px] leading-relaxed text-gray-100">
          {text}
        </pre>
        <ol className="list-decimal pl-5 text-[12px] text-gray-600">
          <li>
            A developer replaces signal_rules.json with this file and opens a
            PR.
          </li>
          <li>
            Once it deploys, these rules match the code: they read Code default
            and the banner clears.
          </li>
          <li>Until then they stay live from here. Nothing applies twice.</li>
        </ol>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className={SECONDARY}
            onClick={() => void navigator.clipboard?.writeText(text)}
          >
            Copy
          </button>
          <button type="button" className={PRIMARY} onClick={download}>
            <Download className="h-4 w-4" aria-hidden />
            Download signal_rules.json
          </button>
        </div>
      </div>
    </Drawer>
  );
};

const RuleClassifierPanel: React.FC<RuleClassifierPanelProps> = ({
  registry,
  config,
  draft,
  onDraftChange,
  organizations,
  orgsLoading,
  orgId,
  onOrgChange,
  onOpenSignal,
}) => {
  const dispatch = useAppDispatch();
  const byId = useMemo(
    () => Object.fromEntries(registry.definitions.map(d => [d.id, d])),
    [registry.definitions]
  );
  const rules = useMemo(
    () => draftRules(registry, config, draft),
    [registry, config, draft]
  );
  const changes = useMemo(
    () => classifierChanges(config, draft),
    [config, draft]
  );
  const changesKey = JSON.stringify(changes);

  const [tab, setTab] = useState<Tab>("decide");
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [editing, setEditing] = useState<{
    id: string;
    isNew: boolean;
    record: ClassifierRuleRecord;
  } | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [impact, setImpact] = useState<ClassifierImpact | null>(null);
  const [impactState, setImpactState] = useState<"idle" | "loading" | "error">(
    "idle"
  );
  const [impactError, setImpactError] = useState("");

  // With only one organization there's nothing to choose.
  useEffect(() => {
    if (!orgId && organizations.length === 1)
      onOrgChange(organizations[0].org_id);
  }, [orgId, organizations, onOrgChange]);

  // Impact follows the draft, debounced so editing doesn't send a request
  // per keystroke.
  useEffect(() => {
    if (!orgId) {
      setImpact(null);
      setImpactState("idle");
      return undefined;
    }
    let cancelled = false;
    setImpactState("loading");
    const timer = window.setTimeout(() => {
      dispatch(
        fetchClassifierImpact({ orgId, classifier: JSON.parse(changesKey) })
      )
        .unwrap()
        .then(res => {
          if (cancelled) return;
          setImpact(res as ClassifierImpact);
          setImpactState("idle");
        })
        .catch(e => {
          if (cancelled) return;
          setImpactError(errorText(e, "Couldn't work out rule impact."));
          setImpactState("error");
        });
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [dispatch, orgId, changesKey]);

  const name = (id: string) =>
    byId[id] ? `${signalTag(byId[id])} ${byId[id].title}` : id;

  const matches = (r: DraftRule) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [r.id, r.note, ...r.members.map(name)]
      .join(" ")
      .toLowerCase()
      .includes(q);
  };

  const decide = rules.filter(r => r.status === "proposed" && matches(r));
  const inForce = rules.filter(r => r.status === "active" && matches(r));
  const switchedOff = rules.filter(r => r.status === "off" && matches(r));
  const opened = rules.find(r => r.id === openId) ?? null;
  const counts = {
    decide: rules.filter(r => r.status === "proposed").length,
    force: rules.filter(r => r.status === "active").length,
  };

  const stage = (rule: DraftRule, record: ClassifierRuleRecord | null) =>
    onDraftChange(
      stageRule(draft, rule.id, record, rule.code ? toRecord(rule.code) : null)
    );
  const setStatus = (rule: DraftRule, next: ClassifierRuleStatus) =>
    stage(rule, { ...toRecord(rule), status: next });

  const impactLine = (r: DraftRule) => {
    if (!orgId) return null;
    if (!impact)
      return (
        <span className="text-gray-400">
          {impactState === "error"
            ? "Impact unavailable"
            : "Working out impact…"}
        </span>
      );
    const v = impact.rules[r.id];
    if (!v) return null;
    const on = r.status === "active";
    if (v.cards === 0)
      return (
        <span className="text-gray-500">
          {on ? "Changes nothing" : "Would change nothing"} on today&rsquo;s
          cards
        </span>
      );
    const verb = on ? "Hides" : "Would hide";
    return (
      <span className="font-medium text-gray-800">
        {verb} {v.cards} card{v.cards === 1 ? "" : "s"} on {v.accounts} account
        {v.accounts === 1 ? "" : "s"} today
      </span>
    );
  };

  const renderCard = (r: DraftRule) => {
    const problems = r.status === "active" ? [] : ruleProblems(r);
    return (
      <li
        key={r.id}
        className={`rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm ${
          r.status === "off" ? "opacity-70" : ""
        } ${openId === r.id ? "ring-2 ring-brand/30" : ""}`}
      >
        <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
          <button
            type="button"
            onClick={() => setOpenId(r.id)}
            className="mt-0.5 rounded-md bg-gray-100 px-1.5 py-0.5 font-mono text-[11.5px] font-bold text-gray-700 hover:bg-gray-200"
            aria-label={`Open rule ${r.id}`}
          >
            {r.id}
          </button>
          <div className="min-w-0 flex-1">
            <RuleFlow
              rule={r}
              byId={byId}
              draft={draft}
              onOpenSignal={onOpenSignal}
            />
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]">
              {impactLine(r)}
              {problems.length > 0 && (
                <span className="text-amber-700" title={problems.join(" ")}>
                  Can&rsquo;t switch on: {problems[0].replace(/\.$/, "")}
                </span>
              )}
              <span
                className={
                  r.source === "code"
                    ? "text-gray-400"
                    : "font-semibold text-orange-700"
                }
              >
                {SOURCE_TEXT[r.source]}
              </span>
              {r.unapplied && (
                <span className="font-semibold text-violet-700">Unapplied</span>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {r.status === "proposed" ? (
              <>
                <button
                  type="button"
                  className={PRIMARY}
                  disabled={problems.length > 0}
                  title={problems.length ? problems[0] : "Switch this rule on"}
                  onClick={() => setStatus(r, "active")}
                >
                  Accept
                </button>
                <button
                  type="button"
                  className={SECONDARY}
                  onClick={() => setStatus(r, "off")}
                >
                  Reject
                </button>
              </>
            ) : (
              <Toggle
                on={r.status === "active"}
                disabled={r.status !== "active" && problems.length > 0}
                label={`Rule ${r.id}`}
                onChange={on => setStatus(r, on ? "active" : "off")}
              />
            )}
            <button
              type="button"
              className={SECONDARY}
              onClick={() => setOpenId(r.id)}
            >
              Details
            </button>
          </div>
        </div>
      </li>
    );
  };

  const renderSections = (list: DraftRule[]) =>
    TYPE_ORDER.map(type => ({
      type,
      items: list.filter(r => r.type === type),
    }))
      .filter(s => s.items.length > 0)
      .map(s => (
        <section key={s.type} className="flex flex-col gap-2">
          <div>
            <h4 className="text-[12px] font-bold uppercase tracking-wide text-gray-700">
              {registry.classifier.types[s.type]}{" "}
              <span className="font-medium text-gray-400">
                {s.items.length}
              </span>
            </h4>
            <p className="text-[12px] text-gray-500">{TYPE_SECTION[s.type]}</p>
          </div>
          <ul className="flex flex-col gap-2">{s.items.map(renderCard)}</ul>
        </section>
      ));

  const tabs: [Tab, string, number | null][] = [
    ["decide", "Needs a decision", counts.decide],
    ["force", "Rules in force", counts.force],
    ["signal", "By signal", null],
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-[15px] font-bold text-gray-900">
            Rule Classifier
          </h3>
          <p className="mt-0.5 max-w-3xl text-[12.5px] text-gray-500">
            Decides which signals become cards when several fire for one
            account. Every signal is still evaluated and recorded; rules only
            change what users see, in every organization.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className={SECONDARY}
            onClick={() => setExportOpen(true)}
          >
            <Code2 className="h-4 w-4" aria-hidden />
            Export to code
          </button>
          <button
            type="button"
            className={PRIMARY}
            onClick={() =>
              setEditing({
                id: nextRuleId(rules),
                isNew: true,
                record: {
                  type: "supersede",
                  lead: "",
                  members: [],
                  status: "active",
                  note: "",
                },
              })
            }
          >
            <Plus className="h-4 w-4" aria-hidden />
            New rule
          </button>
        </div>
      </div>

      {registry.classifier.not_in_code > 0 && (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-orange-200 bg-orange-50 px-4 py-2.5"
        >
          <span className="text-[12.5px] text-orange-900">
            <strong>
              {registry.classifier.not_in_code} applied change
              {registry.classifier.not_in_code === 1 ? " isn’t" : "s aren’t"} in
              code yet.
            </strong>{" "}
            They&rsquo;re live; export them so a developer can commit
            signal_rules.json.
          </span>
          <button
            type="button"
            className={SECONDARY}
            onClick={() => setExportOpen(true)}
          >
            Export to code
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-gray-200">
        <div
          role="tablist"
          aria-label="Rule Classifier views"
          className="flex gap-1"
        >
          {tabs.map(([id, label, count]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-[13px] ${
                tab === id
                  ? "border-brand font-semibold text-brand-dark"
                  : "border-transparent text-gray-600 hover:text-gray-900"
              }`}
            >
              {label}
              {count !== null && (
                <span
                  className={`rounded-full px-1.5 text-[11px] font-semibold ${
                    id === "decide" && count > 0
                      ? "bg-sky-100 text-sky-800"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 pb-2">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              aria-hidden
            />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search #14, title or rule"
              aria-label="Search rules"
              className="h-8 w-[200px] rounded-lg border border-gray-300 pl-8 pr-2 text-[12.5px] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            />
          </div>
          <span className="text-[12px] text-gray-500">Impact in</span>
          <OrgSelect
            organizations={organizations}
            value={orgId}
            onChange={onOrgChange}
            loading={orgsLoading}
            size="sm"
            emptyLabel="Pick an organization…"
          />
          {impactState === "loading" && (
            <Loader2
              className="h-4 w-4 animate-spin text-gray-400"
              aria-label="Updating impact"
            />
          )}
        </div>
      </div>

      {impactState === "error" && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
          {impactError}
        </div>
      )}

      {tab === "decide" && (
        <div className="flex flex-col gap-5">
          {decide.length === 0 ? (
            <EmptyState
              title="Nothing needs a decision"
              body="Every proposed rule has been accepted or rejected. New proposals arrive with signal_rules.json in a release."
            />
          ) : (
            renderSections(decide)
          )}
        </div>
      )}

      {tab === "force" && (
        <div className="flex flex-col gap-5">
          {inForce.length === 0 && switchedOff.length === 0 ? (
            <EmptyState
              title="No rules in force"
              body="Every signal shows as its own card. Accept a proposal or add a rule."
            />
          ) : (
            <>
              {renderSections(inForce)}
              {switchedOff.length > 0 && (
                <section className="flex flex-col gap-2">
                  <h4 className="text-[12px] font-bold uppercase tracking-wide text-gray-500">
                    Switched off{" "}
                    <span className="font-medium text-gray-400">
                      {switchedOff.length}
                    </span>
                  </h4>
                  <ul className="flex flex-col gap-2">
                    {switchedOff.map(renderCard)}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
      )}

      {tab === "signal" && (
        <BySignal
          rules={rules.filter(matches)}
          byId={byId}
          draft={draft}
          onOpen={setOpenId}
          onOpenSignal={onOpenSignal}
        />
      )}

      {opened && !editing && (
        <RuleDrawer
          rule={opened}
          registry={registry}
          byId={byId}
          draft={draft}
          impact={impact}
          orgId={orgId}
          changes={changes}
          onClose={() => setOpenId(null)}
          onOpenSignal={onOpenSignal}
          onStatus={next => setStatus(opened, next)}
          onReset={() => stage(opened, null)}
          onEdit={() =>
            setEditing({
              id: opened.id,
              isNew: false,
              record: toRecord(opened),
            })
          }
        />
      )}

      {editing && (
        <RuleEditor
          key={editing.id}
          ruleId={editing.id}
          isNew={editing.isNew}
          initial={editing.record}
          registry={registry}
          draft={draft}
          otherRules={rules.filter(r => r.id !== editing.id)}
          onCancel={() => setEditing(null)}
          onSave={record => {
            const rule = rules.find(r => r.id === editing.id);
            onDraftChange(
              stageRule(
                draft,
                editing.id,
                record,
                rule?.code ? toRecord(rule.code) : null
              )
            );
            setEditing(null);
            setOpenId(editing.id);
            setTab(record.status === "proposed" ? "decide" : "force");
          }}
        />
      )}

      <ExportDrawer
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        registry={registry}
        name={name}
      />
    </div>
  );
};

export default RuleClassifierPanel;
